import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  HostListener,
  Input,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageBubbleComponent, MessageType } from 'shared/components/molecules';
import { TaskSubType } from 'app/shared/enums';
import { NoMessagesComponent, ScrollIndicatorComponent } from 'shared/components/atoms';

export interface Message {
  type: MessageType;
  content: string;
  sender: string;
  timestamp: string;
  subtype: TaskSubType | string;
}

@Component({
  imports: [CommonModule, MessageBubbleComponent, NoMessagesComponent, ScrollIndicatorComponent],
  selector: 'app-message-list',
  templateUrl: './message-list.component.html',
  styles: `
    :host {
      position: relative;
      width: 100%;
      height: 60%;
      flex-grow: 1;
      display: flex;
      flex-direction: column;
    }
  `,
})
export class MessageListComponent implements AfterViewChecked, AfterViewInit {
  @Input() public messages: Message[] = [];
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  @HostListener('window:resize')
  public onResize(): void {
    if (this.isNearBottom) {
      this.scrollToBottom();
    }
  }

  private shouldScroll = true;

  public showScrollIndicator = false;
  private isNearBottom = true;

  // Signal for container height (updates when necessary)
  private containerHeight = signal(0);

  // Computed signal for dynamic threshold
  private dynamicThreshold = computed(() => {
    return Math.min(50, this.containerHeight() * 0.1); // 10% of height, capped at 50px
  });

  public ngAfterViewChecked(): void {
    if (this.shouldScroll) {
      this.scrollToBottom();
    }
  }

  public ngAfterViewInit(): void {
    const element = this.scrollContainer.nativeElement;

    // Update the container height signal on initialization
    this.containerHeight.set(element.clientHeight);

    // Optional: Observe size changes dynamically if the container can resize

    // TOOD: ResizeObserver - undefined, package missing or what?

    // new ResizeObserver(() => {
    //   this.containerHeight.set(element.clientHeight);
    // }).observe(element);
  }

  private scrollToBottom(): void {
    try {
      const container = this.scrollContainer.nativeElement;
      container.scrollTop = container.scrollHeight;
      this.shouldScroll = false;
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  public onScroll(): void {
    const element = this.scrollContainer.nativeElement;
    if (!element) return;
    this.isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < this.dynamicThreshold();
    this.showScrollIndicator = element.scrollTop > this.dynamicThreshold();
  }

  public getMessageVariant(currentIndex: number): 'default' | 'highlighted' {
    const lastIndex = this.messages.length - 1;
    const currentMessage = this.messages[currentIndex];
    const lastMessage = this.messages[lastIndex];

    if (currentMessage.type !== 'other') {
      return 'default';
    }

    // Highlight if it's the last message
    if (lastMessage.type !== 'other') {
      return 'default';
    }

    // Highlight if next message is from the same sender and is also 'other' type
    if (currentIndex === lastIndex) {
      return 'highlighted';
    }

    // For consecutive messages from the same sender, they should match the last message's style
    const nextMessage = this.messages[currentIndex + 1];
    if (nextMessage?.type === 'other' && nextMessage?.sender === currentMessage.sender) {
      return this.getMessageVariant(currentIndex + 1);
    }

    return 'default';
  }
}
